import { Component, input, computed } from '@angular/core';
import { ComponentVariant, ComponentSize } from '../types';

@Component({
  selector: 'app-progress-bar',
  imports: [],
  template: `
    <div class="w-full">
      @if (label()) {
        <div class="flex items-center justify-between mb-2">
          <label class="text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ label() }}
          </label>
          @if (showValue()) {
            <span class="text-sm font-medium text-gray-600 dark:text-gray-400">
              {{ value() }}%
            </span>
          }
        </div>
      }
      
      <div 
        [class]="trackClasses"
        [attr.role]="'progressbar'"
        [attr.aria-valuenow]="value()"
        [attr.aria-valuemin]="0"
        [attr.aria-valuemax]="100"
        [attr.aria-label]="label() || 'Progress'">
        <div 
          [class]="barClasses"
          [style.width.%]="value()"
          [style.transition]="'width 0.3s ease'">
        </div>
      </div>
      
      @if (showValue() && !label()) {
        <div class="text-xs text-gray-500 dark:text-gray-400 text-right mt-1">
          {{ value() }}%
        </div>
      }
    </div>
  `,
  styles: []
})
export class ProgressBarComponent {
  value = input<number>(0);
  label = input<string>('');
  showValue = input<boolean>(true);
  variant = input<ComponentVariant>('primary');
  size = input<ComponentSize>('md');
  striped = input<boolean>(false);
  animated = input<boolean>(false);

  get trackClasses(): string {
    const sizeMap = {
      xs: 'h-1',
      sm: 'h-1.5',
      md: 'h-2',
      lg: 'h-3',
      xl: 'h-4'
    };

    return [
      'w-full',
      'bg-gray-200',
      'dark:bg-gray-700',
      'rounded-full',
      'overflow-hidden',
      sizeMap[this.size()]
    ].join(' ');
  }

  get barClasses(): string {
    const classes = [
      'h-full',
      'rounded-full',
      'transition-all',
      'duration-300'
    ];

    const variantMap: Record<ComponentVariant, string> = {
      success: 'bg-green-500 dark:bg-green-600',
      error: 'bg-red-500 dark:bg-red-600',
      warning: 'bg-yellow-500 dark:bg-yellow-600',
      info: 'bg-blue-500 dark:bg-blue-600',
      primary: 'bg-blue-600 dark:bg-blue-500',
      secondary: 'bg-gray-500 dark:bg-gray-400',
      danger: 'bg-red-500 dark:bg-red-600'
    };
    classes.push(variantMap[this.variant()] || variantMap.primary);

    if (this.striped()) {
      classes.push('bg-stripes');
    }

    if (this.animated()) {
      classes.push('animate-pulse');
    }

    return classes.join(' ');
  }
}

